import { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Progress,
  addToast,
} from '@heroui/react';
import { FaStar } from 'react-icons/fa';
import { FiUpload } from 'react-icons/fi';
// import { useAuth } from 'react-oidc-context';
import CustomButton from '../CustomButton';

import {
  uploadToS3WithImageName,
  deleteFromS3ByImageName,
  getImageUrlsFromNames,
} from '../../frontend/Customer/aws/s3FileUpload';
import { useAuth } from '../../../context/AuthContext';
import { ImageUploadResult, Review } from '../../../utils/type';
import {
  useCreateReview,
  useUpdateReview,
} from '../../../hook/useMutationData';
import { useFetchReviewsById } from '../../../hook/useQueryData';
import LoadingSpinner from '../common/loading/LoadingSprinner';

interface ReviewFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (reviewData: Review) => Promise<void>;
  onSuccess?: () => void;
  reviewId?: string;
  providerId?: string;
  serviceId?: string;
  bookingId?: string;
  /**
   * Quote-flow counterpart of `bookingId`, set when reviewing a completed job.
   * A review carries at most one of the two. Do not pass a job reference as
   * `bookingId` — the backend looks that up as a booking and rejects it.
   */
  jobId?: string;
  businessId?: string;
  serviceName?: string;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const ReviewForm = ({
  isOpen,
  onClose,
  onSuccess,
  reviewId,
  providerId,
  serviceId,
  bookingId,
  jobId,
  businessId,
  serviceName,
  isEdit = false,
  isSubmitting: externalIsSubmitting,
}: ReviewFormProps) => {
  const auth = useAuth();
  // `rating` now holds the EXACT (possibly decimal) overall rating for display purposes.
  // It is only rounded to an integer at the point of submission to the backend.
  const [rating, setRating] = useState<number>(0);
  const [title, setTitle] = useState<string>('');
  const [review, setReview] = useState<string>('');
  const [images, setImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageNames, setImageNames] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const actualIsSubmitting = externalIsSubmitting ?? isSubmitting;
  const [errors, setErrors] = useState<{
    title?: string;
    review?: string;
    images?: string;
    form?: string;
    rating?: string;
  }>({});
  const [uploadingImages, setUploadingImages] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const {
    mutateAsync: mutateAsyncUpdateReview,
    isPending: isPendingUpdateReview,
  } = useUpdateReview();
  const {
    mutateAsync: mutateAsyncCreateReview,
    isPending: isPendingCreateReview,
  } = useCreateReview(serviceId);
  const { data: reviewData, isLoading: isLoadingReviewData } =
    useFetchReviewsById(reviewId);
  const [serviceRating, setServiceRating] = useState<number>(0);
  const [qualityRating, setQualityRating] = useState<number>(0);
  const [valueRating, setValueRating] = useState<number>(0);
  const [communicationRating, setCommunicationRating] = useState<number>(0);
  const [timelinessRating, setTimelinessRating] = useState<number>(0);

  // cleanup function
  const resetForm = () => {
    setRating(0);
    setTitle('');
    setReview('');
    setImages([]);
    setImageUrls([]);
    setImageNames([]);
    setErrors({});
    setServiceRating(0);
    setQualityRating(0);
    setValueRating(0);
    setCommunicationRating(0);
    setTimelinessRating(0);
    setUploadingImages(false);
    setUploadProgress(0);
  };

  // handleClose function
  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!reviewData && !isEdit) {
      setErrors({});
      setTitle(serviceName || '');
      return;
    }
    console.log('reviewForm', reviewData?.review);
    console.log(reviewData?.review.title);

    // Keep the exact decimal value from the backend (clamped to 5) instead of
    // rounding it away, so fractional overall ratings render correctly.
    const normalizedOverallRating =
      reviewData?.review?.rating && reviewData?.review?.rating >= 1
        ? Math.min(reviewData?.review.rating, 5)
        : 0;

    setRating(normalizedOverallRating);
    setTitle(reviewData?.review.title || '');
    setReview(reviewData?.review.comment || '');

    const initImageNames = reviewData?.review.imageNames || [];
    setImageNames(initImageNames);
    setImageUrls(
      initImageNames.length > 0
        ? getImageUrlsFromNames(initImageNames, 'review-images')
        : []
    );

    // Sub-aspect ratings stay whole numbers since they're chosen via discrete stars.
    const derive = (v?: number) =>
      v && v >= 1 && v <= 5
        ? Math.round(v)
        : Math.round(normalizedOverallRating);

    setServiceRating(derive(reviewData?.review.serviceRating));
    setQualityRating(derive(reviewData?.review.qualityRating));
    setValueRating(derive(reviewData?.review.valueRating));
    setCommunicationRating(derive(reviewData?.review.communicationRating));
    setTimelinessRating(derive(reviewData?.review.timelinessRating));
  }, [isOpen, reviewId, serviceName, reviewData, isEdit]);

  // Calculate overall rating based on the 5 specific ratings (including timelinessRating).
  // The average is kept as-is (decimal) so the UI can show e.g. 4.2 instead of
  // silently rounding to 4.
  useEffect(() => {
    const ratings = [
      serviceRating,
      qualityRating,
      valueRating,
      communicationRating,
      timelinessRating,
    ].filter((r) => r > 0);
    if (ratings.length > 0) {
      const average = ratings.reduce((a, b) => a + b, 0) / ratings.length;
      setRating(average);
      setErrors((prev) => ({ ...prev, rating: undefined }));
    } else {
      setRating((prev) => (prev > 0 ? prev : 0));
    }
  }, [
    serviceRating,
    qualityRating,
    valueRating,
    communicationRating,
    timelinessRating,
  ]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      const totalImages = imageUrls.length + newFiles.length;
      if (totalImages > 5) {
        setErrors((prev) => ({
          ...prev,
          images: 'You can upload a maximum of 5 images.',
        }));
        return;
      }

      setUploadingImages(true);
      setUploadProgress(0);
      setErrors((prev) => ({ ...prev, images: undefined }));

      try {
        const uploadedUrls: string[] = [];
        const uploadedNames: string[] = [];
        const uploadResults: ImageUploadResult[] = [];

        console.log(`Starting upload of ${newFiles.length} files to S3...`);

        for (let i = 0; i < newFiles.length; i++) {
          const file = newFiles[i];
          const progress = ((i + 1) / newFiles.length) * 100;
          setUploadProgress(progress);

          console.log(
            `Uploading file ${i + 1}/${newFiles.length}: ${file.name}`
          );

          const uploadResult = await uploadToS3WithImageName(
            file,
            'review-images'
          );

          uploadedUrls.push(uploadResult.fullUrl);
          uploadedNames.push(uploadResult.imageName);
          uploadResults.push(uploadResult);

          console.log(`File uploaded successfully:`, {
            originalName: file.name,
            imageName: uploadResult.imageName,
            url: uploadResult.fullUrl,
          });
        }

        setImageUrls((prev) => [...prev, ...uploadedUrls]);
        setImageNames((prev) => [...prev, ...uploadedNames]);
        setImages((prev) => [...prev, ...newFiles]);

        console.log('All files uploaded successfully:', {
          totalFiles: newFiles.length,
          imageNames: uploadedNames,
          imageUrls: uploadedUrls,
        });
      } catch (error) {
        console.error('Error uploading images to :', error);
        setErrors((prev) => ({
          ...prev,
          images:
            'File size exceeds the 5MB limit. Please compress your image and try again.',
        }));
      } finally {
        setUploadingImages(false);
        setUploadProgress(0);
      }
    }
  };

  const removeImage = async (index: number) => {
    if (index < 0 || index >= imageNames.length) {
      console.error('Invalid image index for removal:', index);
      return;
    }

    const imageName = imageNames[index];
    const imageUrl = imageUrls[index];

    console.log(`Removing image at index ${index}:`, {
      imageName,
      imageUrl,
    });

    try {
      if (imageName) {
        await deleteFromS3ByImageName(imageName, 'review-images');
        console.log(`Successfully deleted ${imageName} from `);
      }
    } catch (error) {
      console.error('Error deleting image from :', error);
    }

    const newImageUrls = [...imageUrls];
    const newImageNames = [...imageNames];
    const newImages = [...images];

    newImageUrls.splice(index, 1);
    newImageNames.splice(index, 1);
    newImages.splice(index, 1);

    setImageUrls(newImageUrls);
    setImageNames(newImageNames);
    setImages(newImages);

    setErrors((prev) => ({ ...prev, images: undefined }));

    console.log('Image removed from UI:', {
      remainingImages: newImageNames.length,
      remainingNames: newImageNames,
    });
  };

  const validateForm = () => {
    const newErrors: {
      title?: string;
      review?: string;
      images?: string;
      rating?: string;
    } = {};

    const hasAnyRating = [
      serviceRating,
      qualityRating,
      valueRating,
      communicationRating,
      timelinessRating,
    ].some((r) => r > 0);
    if (!hasAnyRating) {
      newErrors.rating =
        'Please provide at least one rating below (Service Quality, Work Quality, Value for Money, Communication, or Timeliness)';
    }

    if (hasAnyRating && (rating < 1 || rating > 5)) {
      newErrors.rating = 'Overall rating must be between 1 and 5';
    }

    if (rating < 1) {
      newErrors.rating =
        'Please provide at least one rating to calculate the overall rating';
    }

    if (!title.trim()) newErrors.title = 'Please enter a review title';
    if (!review.trim()) newErrors.review = 'Please write your review';
    const trimmedReview = review.trim();
    if (!trimmedReview) {
      newErrors.review = 'Review is required and cannot be empty';
    } else if (trimmedReview.length < 10) {
      newErrors.review = 'Review must be at least 10 characters long';
    } else if (trimmedReview.length > 2000) {
      newErrors.review = 'Review must be no more than 2000 characters long';
    }

    if (imageUrls.length > 5)
      newErrors.images = 'You can upload a maximum of 5 images.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeRating = (value: number, fallback: number) => {
    if (value >= 1 && value <= 5) return Math.round(value);
    return fallback;
  };

  // Formats the (possibly decimal) overall rating for display: whole numbers
  // show without a decimal point, fractional values show one decimal place.
  const formatRating = (value: number) =>
    Number.isInteger(value) ? value.toString() : value.toFixed(1);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const sid = serviceId || reviewData?.review?.serviceId;
      if (!sid || typeof sid !== 'string') {
        setErrors((p) => ({ ...p, form: 'Missing or invalid serviceId' }));
        return;
      }

      const hasIndividualRatings = [
        serviceRating,
        qualityRating,
        valueRating,
        communicationRating,
        timelinessRating,
      ].some((r) => r > 0);

      const effectiveRating = rating === 0 && hasIndividualRatings ? 1 : rating;

      if (effectiveRating < 1 || effectiveRating > 5) {
        setErrors((p) => ({
          ...p,
          form: 'Rating must be between 1 and 5',
        }));
        return;
      }

      if (!review || review.length < 10 || review.length > 2000) {
        setErrors((p) => ({
          ...p,
          form: 'Review must be between 10 and 2000 characters.',
        }));
        return;
      }

      const fallbackRating = Math.max(Math.round(effectiveRating), 1);

      const userInfo = auth.user
        ? {
            userName:
              (auth.user as any)?.name || (auth.user as any)?.email || 'User',
            userEmail: (auth.user as any)?.email || '',
            userProfileImage: (auth.user as any)?.picture || '',
            isVerified: (auth.user as any)?.email_verified || false,
          }
        : {};

      const validBookingId =
        bookingId &&
        bookingId !== 'undefined' &&
        bookingId !== 'null' &&
        String(bookingId).trim() !== ''
          ? String(bookingId)
          : undefined;

      // Same treatment for the quote-flow job reference.
      const validJobId =
        jobId &&
        jobId !== 'undefined' &&
        jobId !== 'null' &&
        String(jobId).trim() !== ''
          ? String(jobId)
          : undefined;

      const payload: Record<string, any> = {
        providerId: providerId || 'default-provider',
        serviceId: String(sid),
        // Rounded only here, at submission time, so the backend still receives
        // the integer it expects while the UI can keep showing decimals.
        rating: Math.round(effectiveRating),
        title,
        comment: review,
        date: reviewData?.review?.reviewDate || new Date().toISOString(),
        imageNames,

        serviceRating: normalizeRating(serviceRating, fallbackRating),
        qualityRating: normalizeRating(qualityRating, fallbackRating),
        valueRating: normalizeRating(valueRating, fallbackRating),
        communicationRating: normalizeRating(
          communicationRating,
          fallbackRating
        ),
        timelinessRating: normalizeRating(timelinessRating, fallbackRating),

        ...userInfo,
      };

      if (validBookingId) {
        payload.bookingId = validBookingId;
      }

      // Only include jobId when reviewing a quote job. Sent as its own field,
      // never folded into bookingId.
      if (validJobId) {
        payload.jobId = validJobId;
      }

      // Include businessId when available (derived from the service context)
      if (businessId && String(businessId).trim() !== '') {
        payload.businessId = String(businessId).trim();
      }

      const ratingFields = [
        payload.serviceRating,
        payload.qualityRating,
        payload.valueRating,
        payload.communicationRating,
        payload.timelinessRating,
      ];

      if (ratingFields.some((r) => r < 1 || r > 5)) {
        setErrors((p) => ({
          ...p,
          form: 'All rating fields must be between 1 and 5.',
        }));
        return;
      }

      if (isEdit && reviewData?.review?.reviewId) {
        await mutateAsyncUpdateReview({
          reviewId: reviewData?.review?.reviewId,
          data: payload,
        });
      } else {
        await mutateAsyncCreateReview({ data: payload });
      }

      addToast({
        title: `Review ${isEdit ? 'Updated' : 'Submitted'}`,
        description: `Review ${isEdit ? 'updated' : 'submitted'} successfully!`,
        color: 'success',
        radius: 'md',
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Review submit failed:', err);

      let errorMessage = 'Failed to submit review. Please try again.';

      if (err?.response?.data?.errors?.length) {
        errorMessage = err.response.data.errors
          .map((e: any) => e.message)
          .join(', ');
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setErrors((prev) => ({
        ...prev,
        form: errorMessage,
      }));
    } finally {
      setIsSubmitting(false);
      resetForm();
    }
  };

  const StarRating = ({
    label,
    value,
    onChange,
    disabled = false,
  }: {
    label: string;
    value: number;
    onChange: (val: number) => void;
    disabled?: boolean;
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`w-6 h-6 transition-colors ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            } ${
              value >= star
                ? 'text-yellow-400 hover:text-yellow-500'
                : 'text-gray-300 hover:text-gray-400'
            } ${disabled ? 'pointer-events-none' : ''}`}
            onClick={() => {
              if (!disabled) {
                onChange(star);
                setErrors((prev) => ({ ...prev, rating: undefined }));
              }
            }}
          />
        ))}
      </div>
      {value > 0 && (
        <p className="text-xs text-gray-600">{value} out of 5 stars</p>
      )}
      {disabled && (
        <p className="text-xs text-gray-400 italic">Ratings cannot be edited</p>
      )}
    </div>
  );

  // Renders 5 stars where each one can be partially filled (e.g. a rating of
  // 4.2 fills the 5th star to 20%), instead of only ever showing whole stars.
  const OverallStars = ({
    value,
    size = 'w-6 h-6',
  }: {
    value: number;
    size?: string;
  }) => (
    <div className="flex justify-center space-x-1 mb-3">
      {[1, 2, 3, 4, 5].map((star) => {
        const fillPercent = Math.max(0, Math.min(1, value - (star - 1))) * 100;
        return (
          <span key={star} className="relative inline-block leading-none">
            <FaStar className={`${size} text-gray-300`} />
            <span
              className="absolute top-0 left-0 overflow-hidden"
              style={{ width: `${fillPercent}%` }}
            >
              <FaStar className={`${size} text-yellow-500`} />
            </span>
          </span>
        );
      })}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <ModalContent className="mx-auto w-full max-w-3xl rounded-xl shadow-lg max-h-[85vh] overflow-hidden flex flex-col">
        <ModalHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <div className="w-full flex flex-col gap-3">
            <div className="flex flex-col gap-1 text-left md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-1 text-left">
                <h2 className="text-2xl font-bold text-gray-800">
                  {isEdit ? 'Edit Your Review' : 'Share Your Experience'}
                </h2>
                {title && (
                  <p className="text-sm text-gray-600 font-medium">{title}</p>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Your feedback helps others make informed decisions
            </p>
          </div>
        </ModalHeader>

        <ModalBody className="px-6 md:px-8 pb-6 pt-4 overflow-y-auto flex-grow">
          {isLoadingReviewData ? (
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-8">
              {errors.form && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-600 font-medium">
                      {errors.form}
                    </p>
                  </div>
                </div>
              )}

              {/* Specific Ratings Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  Ratings
                  {isEdit && (
                    <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                      View Only
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {isEdit
                    ? 'Your original ratings are displayed below and cannot be modified'
                    : 'Rate at least one aspect to calculate your overall rating above (1-5 stars)'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <StarRating
                    label="Service Quality"
                    value={serviceRating}
                    onChange={setServiceRating}
                    disabled={isEdit}
                  />
                  <StarRating
                    label="Work Quality"
                    value={qualityRating}
                    onChange={setQualityRating}
                    disabled={isEdit}
                  />
                  <StarRating
                    label="Value for Money"
                    value={valueRating}
                    onChange={setValueRating}
                    disabled={isEdit}
                  />
                  <StarRating
                    label="Communication"
                    value={communicationRating}
                    onChange={setCommunicationRating}
                    disabled={isEdit}
                  />
                  <StarRating
                    label="Timeliness"
                    value={timelinessRating}
                    onChange={setTimelinessRating}
                    disabled={isEdit}
                  />
                </div>

                {isEdit && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Ratings cannot be edited after submission. You can only
                      modify the title and review text.
                    </p>
                  </div>
                )}
              </div>

              {/* Review Content Section */}
              <div>
                <Textarea
                  label="Your Detailed Review"
                  type="textarea"
                  labelPlacement="outside"
                  size="sm"
                  radius="sm"
                  classNames={{
                    label: 'text-body',
                    description: 'text-body',
                    input: 'text-body',
                  }}
                  variant="bordered"
                  placeholder="Share your experience in detail. What did you like? How was the quality? Would you recommend this service?"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  isInvalid={!!errors.review}
                  errorMessage={errors.review}
                  className="w-full"
                  minRows={4}
                  description={`${review.length}/2000 characters (minimum 10 characters required)`}
                />
              </div>

              {/* Image Upload Section */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiUpload className="w-5 h-5 mr-2 text-blue-500" />
                  Add Photos (Optional)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload up to 5 images to showcase the work or service quality.
                  Photos help other customers see the results.
                </p>

                {uploadingImages && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-600">
                        Uploading{' '}
                      </span>
                      <span className="text-sm text-gray-600">
                        {Math.round(uploadProgress)}%
                      </span>
                    </div>
                    <Progress
                      value={uploadProgress}
                      className="w-full"
                      color="primary"
                      size="sm"
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mb-4">
                  {imageUrls.map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={url}
                        className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
                        alt={`Review image ${idx + 1}`}
                      />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 text-white bg-red-500 hover:bg-red-600 rounded-full w-6 h-6 text-sm flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        type="button"
                        aria-label="Remove image"
                        disabled={uploadingImages}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                {imageUrls?.length < 5 && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className={`cursor-pointer flex flex-col items-center ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm font-medium text-gray-600">
                        {uploadingImages
                          ? 'Uploading...'
                          : 'Click to upload images'}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF up to 5MB each
                      </span>
                    </label>
                  </div>
                )}

                {errors.images && (
                  <p className="mt-2 text-sm text-red-600">{errors.images}</p>
                )}
              </div>

              {/* Overall Rating Section */}
              <div
                className={`bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-md border border-yellow-200 ${errors.rating ? 'border-2 border-red-300' : ''}`}
              >
                <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center justify-center">
                  <FaStar className="mr-2 text-yellow-500 w-4 h-4" />
                  Overall Rating
                  {isEdit && (
                    <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                      Read Only
                    </span>
                  )}
                </h3>

                <OverallStars value={rating} />

                {rating > 0 ? (
                  <p className="text-center text-md font-semibold text-gray-800 mb-2">
                    {formatRating(rating)} out of 5 stars
                  </p>
                ) : (
                  <p className="text-center text-md text-gray-500 mb-2">
                    Rate the aspects below to see your overall rating
                  </p>
                )}
                <p className="text-xs text-gray-600 text-center">
                  {isEdit
                    ? 'Ratings cannot be modified after submission'
                    : 'Automatically calculated from your ratings below'}
                </p>

                {errors.rating && (
                  <p className="text-center text-xs text-red-600 mt-2 font-medium">
                    {errors.rating}
                  </p>
                )}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter className="px-6 pb-4">
          <CustomButton label="Cancel" color="danger" onPress={handleClose} />
          <CustomButton
            label={
              actualIsSubmitting
                ? 'Submitting...'
                : isEdit
                  ? 'Update Review'
                  : 'Submit Review'
            }
            color="primary"
            isLoading={
              actualIsSubmitting ||
              isPendingUpdateReview ||
              isPendingCreateReview
            }
            isDisabled={
              actualIsSubmitting ||
              isPendingUpdateReview ||
              isPendingCreateReview
            }
            onPress={handleSubmit}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReviewForm;
